import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Drawer,
  IconButton,
  Button,
  Tooltip,
  TextField,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DoNotDisturbOnRoundedIcon from "@mui/icons-material/DoNotDisturbOnRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import ShoppingBagRoundedIcon from "@mui/icons-material/ShoppingBagRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import axios from "axios";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";

const Coupons = () => {
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);

  // Drawer state
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  // Search
  const [query, setQuery] = useState("");

  const getCoupons = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API.BASE_URL}coupons?isCouponAllocated=false&page=1&limit=20`, {
        headers,
      });

      const list = res?.data?.data?.coupons;
      setCoupons(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to fetch offers", {
        position: "top-right",
        autoClose: 3000,
        theme: "colored",
      });
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const isExpired = (c) => {
    if (!c?.validityEndDate) return false;
    const end = new Date(c.validityEndDate);
    if (Number.isNaN(end.getTime())) return false;
    return end.getTime() < Date.now();
  };

  const getCouponBadge = (c) => {
    if (c?.isBogoCoupon) return { label: "BOGO", color: "secondary" };
    if (c?.isFreebieCoupon) return { label: "FREEBIE", color: "success" };
    if (c?.isCashbackCoupon) return { label: "CASHBACK", color: "info" };
    if (c?.isMrpDiscount) return { label: "MRP DISCOUNT", color: "warning" };
    if (c?.discountPrice != null)
      return { label: `₹${c.discountPrice}`, color: "primary" };
    if (c?.discountPercent != null)
      return { label: `${c.discountPercent}% OFF`, color: "primary" };
    if (c?.discountAmount != null)
      return { label: `₹${c.discountAmount} OFF`, color: "primary" };
    return { label: "OFFER", color: "default" };
  };

  /**
   * Ticket theme per coupon type.
   * We avoid hard-coding brand colors; we use MUI palette tokens in sx.
   */
  const getTicketTheme = (c) => {
    const expired = isExpired(c);
    const inactive = String(c?.status || "").toUpperCase() !== "ACTIVE";

    // base theme key
    let key = "primary";
    if (c?.isFreebieCoupon) key = "success";
    else if (c?.isCashbackCoupon) key = "info";
    else if (c?.isBogoCoupon) key = "secondary";
    else if (c?.isMrpDiscount) key = "warning";
    else if (c?.isEventCoupon) key = "secondary";

    // soften for inactive/expired
    const muted = expired || inactive;

    return {
      key, // palette key
      muted,
      statusText: expired ? "EXPIRED" : inactive ? "INACTIVE" : "ACTIVE",
    };
  };

  const openDrawer = (c) => {
    setSelected(c);
    setOpen(true);
  };

  const closeDrawer = () => {
    setOpen(false);
  };

  const copyCode = async (code) => {
    try {
      if (!code) return;
      await navigator.clipboard.writeText(code);
      toast.success("Coupon code copied!", {
        position: "top-right",
        autoClose: 2000,
        theme: "colored",
      });
    } catch (e) {
      toast.error("Failed to copy", {
        position: "top-right",
        autoClose: 2000,
        theme: "colored",
      });
    }
  };

  const filteredCoupons = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return coupons;
    return coupons.filter((c) => {
      const title = (c?.title || "").toLowerCase();
      const code = (c?.couponCode || "").toLowerCase();
      const desc = (c?.description || "").toLowerCase();
      return title.includes(q) || code.includes(q) || desc.includes(q);
    });
  }, [coupons, query]);

  const selectedMeta = useMemo(() => {
    const c = selected;
    if (!c) return null;

    const badge = getCouponBadge(c);

    const categoryNames = Array.isArray(c?.applicableCategories)
      ? c.applicableCategories.map((x) => x?.name).filter(Boolean)
      : [];

    const productNames = Array.isArray(c?.applicableProducts)
      ? c.applicableProducts.map((x) => x?.name).filter(Boolean)
      : [];

    const excludedOutletCount = Array.isArray(c?.excludedOutlets)
      ? c.excludedOutlets.length
      : 0;

    const expired = isExpired(c);
    const inactive = String(c?.status || "").toUpperCase() !== "ACTIVE";

    return {
      badge,
      categoryNames,
      productNames,
      excludedOutletCount,
      expired,
      inactive,
      theme: getTicketTheme(c),
    };
  }, [selected]);

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>
            Coupons
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {loading ? "Loading..." : `${filteredCoupons.length} found`}
          </Typography>
        </Box>

        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by title / code"
          size="small"
          sx={{ width: { xs: "100%", sm: 360 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {/* Body */}
      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 9 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Skeleton variant="text" width="75%" height={26} />
                  <Skeleton variant="text" width="55%" />
                  <Skeleton variant="rounded" height={70} sx={{ mt: 1 }} />
                  <Skeleton variant="text" width="90%" sx={{ mt: 1 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : filteredCoupons.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            No coupons available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try again later.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredCoupons.map((c) => {
            const badge = getCouponBadge(c);
            const theme = getTicketTheme(c);
            const expired = isExpired(c);
            const inactive = theme.muted;
            const isActiveCard =
              (selected?._id || selected?.couponCode) ===
              (c?._id || c?.couponCode);

            return (
              <Grid item xs={12} sm={6} md={4} key={c?._id || c?.couponCode}>
                {/* Ticket Card */}
                <Card
                  variant="outlined"
                  onClick={() => openDrawer(c)}
                  sx={{
                    cursor: "pointer",
                    borderRadius: 3,
                    overflow: "hidden",
                    position: "relative",
                    transition: "0.18s ease",
                    borderColor: isActiveCard ? `${theme.key}.main` : "divider",
                    boxShadow: isActiveCard ? 3 : 0,
                    opacity: inactive ? 0.75 : 1,

                    "&:hover": {
                      boxShadow: 5,
                      transform: "translateY(-2px)",
                      borderColor: `${theme.key}.main`,
                    },

                    // Left accent strip
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: 10,
                      height: "100%",
                      background: (t) =>
                        inactive
                          ? t.palette.action.disabledBackground
                          : t.palette[theme.key].main,
                    },

                    // Ticket "notches" (cut-outs) on left & right
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: "50%",
                      left: 10,
                      right: 0,
                      height: 0,
                    },
                  }}
                >
                  <CardContent sx={{ pl: 2.2 }}>
                    {/* Top row */}
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box sx={{ pr: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 950,
                            lineHeight: 1.2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={c?.title || "Coupon"}
                        >
                          {c?.title || "Coupon"}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          sx={{ mt: 0.6 }}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Chip
                            size="small"
                            icon={<LocalOfferRoundedIcon />}
                            label={c?.couponCode || "—"}
                            variant="outlined"
                            sx={{
                              fontWeight: 800,
                              borderStyle: "dashed",
                              borderColor: `${theme.key}.main`,
                              color: `${theme.key}.dark`,
                              bgcolor: (t) =>
                                inactive
                                  ? t.palette.action.hover
                                  : t.palette[theme.key].light + "22",
                            }}
                          />

                          <Chip
                            size="small"
                            label={badge.label}
                            color={badge.color}
                            sx={{ fontWeight: 900 }}
                          />

                          <Chip
                            size="small"
                            variant="outlined"
                            icon={
                              inactive ? (
                                <DoNotDisturbOnRoundedIcon />
                              ) : (
                                <CheckCircleRoundedIcon />
                              )
                            }
                            label={expired ? "EXPIRED" : c?.status || "—"}
                            sx={{
                              fontWeight: 900,
                              borderStyle: "dashed",
                              opacity: 0.95,
                            }}
                          />
                        </Stack>
                      </Box>

                      {/* Big discount pill */}
                      <Box
                        sx={{
                          px: 1.25,
                          py: 0.6,
                          borderRadius: 2,
                          fontWeight: 950,
                          letterSpacing: 0.2,
                          bgcolor: (t) =>
                            inactive
                              ? t.palette.action.hover
                              : t.palette[theme.key].main,
                          color: (t) =>
                            inactive
                              ? t.palette.text.secondary
                              : t.palette.getContrastText(t.palette[theme.key].main),
                          minWidth: 96,
                          textAlign: "center",
                          alignSelf: "flex-start",
                        }}
                      >
                        {badge.label}
                      </Box>
                    </Stack>

                    {/* Middle description */}
                    <Box
                      sx={{
                        mt: 1.2,
                        p: 1.1,
                        borderRadius: 2,
                        bgcolor: (t) =>
                          inactive
                            ? t.palette.action.hover
                            : t.palette[theme.key].light + "22",
                        border: "1px solid",
                        borderColor: (t) =>
                          inactive ? t.palette.divider : t.palette[theme.key].light,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {c?.description || "Tap to view details"}
                      </Typography>
                    </Box>

                    {/* Perforation line */}
                    <Box
                      sx={{
                        my: 1.4,
                        position: "relative",
                        height: 1,
                        borderTop: "1px dashed",
                        borderColor: (t) =>
                          inactive ? t.palette.divider : t.palette[theme.key].light,
                        // notch circles
                        "&::before, &::after": {
                          content: '""',
                          position: "absolute",
                          top: -10,
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          bgcolor: "background.paper",
                          border: "1px solid",
                          borderColor: "divider",
                        },
                        "&::before": { left: -12 },
                        "&::after": { right: -12 },
                      }}
                    />

                    {/* Bottom meta chips */}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<InfoRoundedIcon />}
                        label={`Min: ${
                          c?.minOrderAmount != null ? `₹${c.minOrderAmount}` : "—"
                        }`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<AccessTimeRoundedIcon />}
                        label={`${formatDate(c?.validityStartDate)} - ${formatDate(
                          c?.validityEndDate
                        )}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Order: ${c?.orderType || "ALL"}`}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Right Drawer (Improved) */}
      <Drawer
        anchor="right"
        open={open}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 460 },
            borderTopLeftRadius: 18,
            borderBottomLeftRadius: 18,
            overflow: "hidden",
          },
        }}
      >
        {/* Header strip */}
        <Box
          sx={{
            px: 2,
            py: 1.6,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: (t) =>
              selectedMeta?.theme?.muted
                ? t.palette.action.hover
                : t.palette[selectedMeta?.theme?.key || "primary"].main,
            color: (t) =>
              t.palette.getContrastText(
                t.palette[selectedMeta?.theme?.key || "primary"].main
              ),
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack spacing={0.2} sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                Coupon Details
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {selected ? "Review eligibility & terms" : "Select a coupon from the list"}
              </Typography>
            </Stack>

            <IconButton
              onClick={closeDrawer}
              sx={{
                color: "inherit",
                bgcolor: "rgba(255,255,255,0.12)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.18)" },
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </Box>

        {!selected ? (
          <Box sx={{ p: 2, py: 6, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Select a coupon to view details.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            {/* Scrollable content */}
            <Box sx={{ p: 2, overflow: "auto", flex: 1 }}>
              {/* Title + code */}
              <Stack spacing={1.2}>
                <Typography variant="h6" sx={{ fontWeight: 950, lineHeight: 1.2 }}>
                  {selected?.title || "Coupon"}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={selectedMeta?.badge?.label || "OFFER"}
                    color={selectedMeta?.badge?.color || "default"}
                    sx={{ fontWeight: 900 }}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Usage: ${selected?.usageType || "—"}`}
                    sx={{ fontWeight: 800 }}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Order: ${selected?.orderType || "ALL"}`}
                    sx={{ fontWeight: 800 }}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={
                      selectedMeta?.expired || selectedMeta?.inactive ? (
                        <DoNotDisturbOnRoundedIcon />
                      ) : (
                        <CheckCircleRoundedIcon />
                      )
                    }
                    label={selectedMeta?.expired ? "EXPIRED" : selected?.status || "—"}
                    sx={{ fontWeight: 900 }}
                  />
                </Stack>

                <Box
                  sx={{
                    p: 1.2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: (t) =>
                      t.palette[selectedMeta?.theme?.key || "primary"].light + "1A",
                  }}
                >
                  <Typography variant="body2" sx={{ mb: selected?.description ? 0 : 0 }}>
                    {selected?.description || "—"}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title="Copy code">
                    <Button
                      variant="contained"
                      startIcon={<ContentCopyRoundedIcon />}
                      onClick={() => copyCode(selected?.couponCode)}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 900,
                      }}
                    >
                      {selected?.couponCode || "—"}
                    </Button>
                  </Tooltip>

                  <Typography variant="body2" color="text.secondary">
                    Tap to copy & share
                  </Typography>
                </Stack>
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* Key details */}
              <Section title="Key Details">
                <Row
                  label="Min order"
                  value={
                    selected?.minOrderAmount != null
                      ? `₹${selected.minOrderAmount}`
                      : "—"
                  }
                />
                <Row
                  label="Validity"
                  value={`${formatDate(selected?.validityStartDate)} - ${formatDate(
                    selected?.validityEndDate
                  )}`}
                />
                <Row label="Order type" value={selected?.orderType || "ALL"} />
                <Row label="Usage" value={selected?.usageType || "—"} />
                {selectedMeta?.excludedOutletCount > 0 ? (
                  <Row
                    label="Excluded outlets"
                    value={`${selectedMeta.excludedOutletCount}`}
                  />
                ) : null}
              </Section>

              {/* Categories */}
              {selectedMeta?.categoryNames?.length > 0 ? (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Section title="Applicable Categories" icon={<CategoryRoundedIcon />}>
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      {selectedMeta.categoryNames.map((name) => (
                        <Chip
                          key={name}
                          size="small"
                          label={name}
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Section>
                </>
              ) : null}

              {/* Products */}
              {selectedMeta?.productNames?.length > 0 ? (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Section title="Applicable Products" icon={<ShoppingBagRoundedIcon />}>
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      {selectedMeta.productNames.map((name) => (
                        <Chip
                          key={name}
                          size="small"
                          label={name}
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Section>
                </>
              ) : null}

              {/* Terms */}
              {Array.isArray(selected?.termsAndConditions) &&
              selected.termsAndConditions.length > 0 ? (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Section title="Terms & Conditions">
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {selected.termsAndConditions.map((t, idx) => (
                        <li key={idx}>
                          <Typography variant="body2" sx={{ mb: 0.6 }}>
                            {t}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  </Section>
                </>
              ) : null}
            </Box>

            {/* Sticky bottom actions */}
            <Box
              sx={{
                p: 2,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <Stack direction="row" spacing={1.2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={closeDrawer}
                  sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
                >
                  Close
                </Button>

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<ContentCopyRoundedIcon />}
                  onClick={() => copyCode(selected?.couponCode)}
                  sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
                >
                  Copy
                </Button>
              </Stack>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

const Section = ({ title, icon, children }) => {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        {icon ? (
          <Box sx={{ display: "flex", alignItems: "center", color: "text.secondary" }}>
            {icon}
          </Box>
        ) : null}
        <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
};

const Row = ({ label, value }) => {
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      spacing={2}
      sx={{ py: 0.35 }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 800, textAlign: "right" }}>
        {value}
      </Typography>
    </Stack>
  );
};

export default Coupons;